// React imports
import React, { useState, useRef, useEffect } from "react";

// Self Created Components
import SidepanelSection from "./components/SidepanelSection";
import InputLabel from "./components/InputLabel";
import InputField from "./components/InputField";

// Other libraries
import { Button, IconButton, Spinner } from "@radix-ui/themes";
import imageCompression from "browser-image-compression";
import { ToastContainer, Zoom, toast } from "react-toastify";
import { Cross2Icon, TrashIcon } from "@radix-ui/react-icons";
import {
  calculateGrid,
  fileToJSON,
  getObjectsPerQuantity,
  objectSegementation,
} from "./utils";

function App() {
  const [template, setTemplate] = useState({
    plateWidthInches: 12, // Example: 3 inches
    plateHeightInches: 12,
    productWidthInches: 2,
    productHeightInches: 2,
    outMarginXInches: 0.5,
    outMarginYInches: 0.5,
    gapXInches: 0.2,
    gapYInches: 0.2,
    dpi: 96, // Set DPI (96 for web, 300 for print)
  });
  const [slots, setSlots] = useState([]);
  const [activeTab, setActiveTab] = useState("Template");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [fileUploading, setFileUploading] = useState(false);
  const outputScaleFactor = 5; // Example scale factor for upscaling

  const browserCompressionOptions = {
    maxSizeMB: 0.1,
    maxWidthOrHeight: 200,
  };

  console.log(
    calculateGrid(
      template.plateWidthInches,
      template.plateHeightInches,
      template.productWidthInches,
      template.productHeightInches,
      template.outMarginXInches,
      template.outMarginYInches,
      template.gapXInches,
      template.gapYInches
    )
  );

  useEffect(() => {
    const grid = calculateGrid(
      template.plateWidthInches,
      template.plateHeightInches,
      template.productWidthInches,
      template.productHeightInches,
      template.outMarginXInches,
      template.outMarginYInches,
      template.gapXInches,
      template.gapYInches
    );

    setSlots(grid.blocks);
  }, [template]);

  const renderProducts = () => {
    const products = [];

    const pseudoSelectedFiles = getObjectsPerQuantity(selectedFiles);

    for (let i = 0; i < slots.length; i++) {
      const { x, y, width, height } = slots[i];

      products.push(
        <rect
          key={`${x}-${y}`}
          x={x * template.dpi}
          y={y * template.dpi}
          width={width * template.dpi}
          height={height * template.dpi}
          fill="red"
          stroke="red"
          strokeWidth="1"
        />
      );
      if (pseudoSelectedFiles[i]) {
        const { id, quantity } = pseudoSelectedFiles[i];
        for (let j = 0; j < quantity; j++) {
          products.push(
            <image
              key={`${id}-${x}-${y}-${j}`}
              x={x * template.dpi}
              y={y * template.dpi}
              width={width * template.dpi}
              height={height * template.dpi}
              href={URL.createObjectURL(
                pseudoSelectedFiles[i].compressedContent
              )}
            />
          );
        }
      }
    }

    return products;
  };

  // Function to handle the download of the template

  const handleDownloadTemplate = () => {
    // Create a canvas element
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    // Set canvas dimensions
    canvas.width = template.plateWidthInches * template.dpi;
    canvas.height = template.plateHeightInches * template.dpi;

    // // Draw white background with black border
    // context.fillStyle = "white";
    // context.fillRect(0, 0, canvas.width, canvas.height);
    // context.strokeStyle = "black";
    // context.lineWidth = 2;
    // context.strokeRect(0, 0, canvas.width, canvas.height);

    // Draw product slots
    context.fillStyle = "transparent";
    context.strokeStyle = "red";
    context.lineWidth = 3;

    slots.forEach((slot) => {
      context.fillRect(
        slot.x * template.dpi,
        slot.y * template.dpi,
        slot.width * template.dpi,
        slot.height * template.dpi
      );
      context.strokeRect(
        slot.x * template.dpi,
        slot.y * template.dpi,
        slot.width * template.dpi,
        slot.height * template.dpi
      );
    });

    // Create a link to download the image
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "template.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = async (e) => {
    setFileUploading(true);
    const files = Array.from(e.target.files);
    console.log(files);
    const output = [];

    // Wait for all files to be processed before updating state
    const processFiles = async () => {
      for (const file of files) {
        const compressedContent = await imageCompression(
          file,
          browserCompressionOptions
        );
        const json = fileToJSON(file);
        if (json.error) {
          toast.error(json.error);
          return;
        }
        if (
          json.quantity +
            selectedFiles.reduce((acc, curr) => acc + curr.quantity, 0) >
          slots.length
        ) {
          toast.error("Insufficient slots");
          return;
        }
        output.push({
          id: json.id,
          storeName: json.storeName,
          type: json.type,
          quantity: json.quantity,
          isDoubleSided: json.isDoubleSided,
          name: file.name,
          rawContent: file,
          compressedContent,
        });
      }
    };

    await processFiles();

    setSelectedFiles((prevFiles) => [...prevFiles, ...output]);
    setFileUploading(false);

    e.target.value = null;
  };

  const handleDownloadPrintFile = async () => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    // Set canvas dimensions with scaling
    canvas.width = template.plateWidthInches * template.dpi * outputScaleFactor;
    canvas.height =
      template.plateHeightInches * template.dpi * outputScaleFactor;

    // Clear the canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    // // Draw white background first
    // context.fillStyle = "white";
    // context.fillRect(0, 0, canvas.width, canvas.height);

    // Function to load and draw an image
    const loadImageAndDraw = (file, x, y, width, height) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            context.drawImage(
              img,
              x * outputScaleFactor,
              y * outputScaleFactor,
              width * outputScaleFactor,
              height * outputScaleFactor
            );
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    };

    try {
      // Draw each design on the canvas
      const grid = calculateGrid(
        template.plateWidthInches,
        template.plateHeightInches,
        template.productWidthInches,
        template.productHeightInches,
        template.outMarginXInches,
        template.outMarginYInches,
        template.gapXInches,
        template.gapYInches
      );

      const pseudoSelectedFiles = getObjectsPerQuantity(selectedFiles);
      const drawPromises = [];

      for (let i = 0; i < pseudoSelectedFiles.length; i++) {
        const { x, y, width, height } = grid.blocks[i];
        drawPromises.push(
          loadImageAndDraw(
            pseudoSelectedFiles[i].rawContent,
            x * template.dpi,
            y * template.dpi,
            width * template.dpi,
            height * template.dpi
          )
        );
      }

      // Wait for all images to be drawn
      await Promise.all(drawPromises);

      // Convert canvas to PNG and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "print_file.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch (error) {
      console.error("Error generating print file:", error);
      toast.error("Error generating print file");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="w-screen bg-blue-50 min-h-screen">
      {fileUploading && (
        <div className="fixed top-0 left-0 w-full h-full z-10 bg-gray-600 bg-opacity-80 text-white flex items-center justify-center font-bold text-2xl">
          <Spinner size="3" /> Loading...
        </div>
      )}
      <ToastContainer
        position="top-left"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover={false}
        theme="colored"
        transition={Zoom}
      />
      <div className="min-h-screen w-[calc(100vw-30rem)] flex justify-center items-center">
        <svg
          width={700}
          height={700}
          viewBox={`0 0 ${template.plateWidthInches * template.dpi} ${
            template.plateHeightInches * template.dpi
          }`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x={0}
            y={0}
            width={template.plateWidthInches * template.dpi}
            height={template.plateHeightInches * template.dpi}
            fill="white"
            stroke="black"
            strokeWidth="2"
          />
          {renderProducts()}
        </svg>
      </div>
      <aside className="fixed right-0 top-0 h-screen w-[30rem] rounded-l-lg shadow-lg border border-gray-300 p-6 bg-white">
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => handleTabChange("Template")}
            className={`px-4 py-2 ${
              activeTab === "Template"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Template
          </button>
          <button
            onClick={() => handleTabChange("Print")}
            className={`px-4 py-2 ${
              activeTab === "Print"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            Print
          </button>
        </div>
        {activeTab === "Template" && (
          <SidepanelSection title="Template (Inches)">
            <InputLabel label="Plate Width (inches)">
              <InputField
                value={template.plateWidthInches}
                onChange={(e) =>
                  setTemplate((prevTemplate) => ({
                    ...prevTemplate,
                    plateWidthInches: Number(e.target.value),
                  }))
                }
              />
            </InputLabel>

            <InputLabel label="Plate Height (inches)">
              <InputField
                label="Plate Height (inches)"
                value={template.plateHeightInches}
                onChange={(e) =>
                  setTemplate((prevTemplate) => ({
                    ...prevTemplate,
                    plateHeightInches: Number(e.target.value),
                  }))
                }
              />
            </InputLabel>

            <InputLabel label="Product Width (inches)">
              <InputField
                value={template.productWidthInches}
                onChange={(e) =>
                  setTemplate((prevTemplate) => ({
                    ...prevTemplate,
                    productWidthInches: Number(e.target.value),
                  }))
                }
              />
            </InputLabel>

            <InputLabel label="Product Height (inches)">
              <InputField
                value={template.productHeightInches}
                onChange={(e) =>
                  setTemplate((prevTemplate) => ({
                    ...prevTemplate,
                    productHeightInches: Number(e.target.value),
                  }))
                }
              />
            </InputLabel>

            <InputLabel label="Outer Margin X (inches)">
              <>
                <InputField
                  value={template.outMarginXInches}
                  onChange={(e) =>
                    setTemplate((prevTemplate) => ({
                      ...prevTemplate,
                      outMarginXInches: Number(e.target.value),
                    }))
                  }
                />
                <Button
                  onClick={() => {
                    const grid = calculateGrid(
                      template.plateWidthInches,
                      template.plateHeightInches,
                      template.productWidthInches,
                      template.productHeightInches,
                      template.outMarginXInches,
                      template.outMarginYInches,
                      template.gapXInches,
                      template.gapYInches
                    );

                    const colNum = grid.colNum;
                    const totalProductWidth =
                      template.productWidthInches * colNum;
                    const totalGapWidth = template.gapXInches * (colNum - 1);

                    const outXMargin =
                      (template.plateWidthInches -
                        (totalProductWidth + totalGapWidth)) /
                      2;

                    setTemplate((prevTemplate) => ({
                      ...prevTemplate,
                      outMarginXInches: outXMargin,
                    }));
                  }}
                >
                  X align
                </Button>
              </>
            </InputLabel>

            <InputLabel label="Outer Margin Y (inches)">
              <>
                <InputField
                  value={template.outMarginYInches}
                  onChange={(e) =>
                    setTemplate((prevTemplate) => ({
                      ...prevTemplate,
                      outMarginYInches: Number(e.target.value),
                    }))
                  }
                />
                <Button
                  onClick={() => {
                    const grid = calculateGrid(
                      template.plateWidthInches,
                      template.plateHeightInches,
                      template.productWidthInches,
                      template.productHeightInches,
                      template.outMarginXInches,
                      template.outMarginYInches,
                      template.gapXInches,
                      template.gapYInches
                    );
                    const outYMargin =
                      (template.plateHeightInches -
                        (template.productHeightInches * grid.rowNum +
                          (grid.rowNum - 1) * template.gapYInches)) /
                      2;

                    setTemplate((prevTemplate) => ({
                      ...prevTemplate,
                      outMarginYInches: outYMargin,
                    }));
                  }}
                >
                  Y Align
                </Button>
              </>
            </InputLabel>

            <InputLabel label="Gap X (inches)">
              <InputField
                value={template.gapXInches}
                onChange={(e) =>
                  setTemplate((prevTemplate) => ({
                    ...prevTemplate,
                    gapXInches: Number(e.target.value),
                  }))
                }
              />
            </InputLabel>

            <InputLabel label="Gap Y (inches)">
              <InputField
                value={template.gapYInches}
                onChange={(e) =>
                  setTemplate((prevTemplate) => ({
                    ...prevTemplate,
                    gapYInches: Number(e.target.value),
                  }))
                }
              />
            </InputLabel>
            <div className="mt-6">
              <Button
                onClick={handleDownloadTemplate}
                className="w-full bg-blue-600 text-white hover:bg-blue-700"
              >
                Download Template
              </Button>
            </div>
          </SidepanelSection>
        )}
        {activeTab === "Print" && (
          <SidepanelSection title="Upload Designs">
            {selectedFiles.length === 0 ? (
              fileUploading ? (
                <div className="flex justify-center items-center h-full">
                  <Spinner />
                </div>
              ) : (
                <Button
                  onClick={() => {
                    fileInputRef.current.click();
                  }}
                >
                  Add Design
                </Button>
              )
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Button
                    onClick={() => {
                      fileInputRef.current.click();
                    }}
                  >
                    Add Design
                  </Button>
                  <p className="text-xs font-medium">
                    {selectedFiles.reduce(
                      (acc, current) => acc + current.quantity,
                      0
                    )}{" "}
                    / {slots.length}
                  </p>
                </div>
                <div className="bg-gray-100 p-3 rounded-lg flex flex-col gap-1">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className=" flex items-center justify-between gap-4 "
                    >
                      <img
                        src={URL.createObjectURL(file.compressedContent)}
                        alt={file.name}
                        className="w-12 h-12 mr-4"
                      />
                      <span className="text-xs">{file.name}</span>
                      <IconButton
                        variant="outline"
                        size="1"
                        color="red"
                        onClick={() =>
                          setSelectedFiles((prevFiles) => {
                            const newFiles = [...prevFiles];
                            newFiles.splice(index, 1);
                            return newFiles;
                          })
                        }
                      >
                        <TrashIcon />
                      </IconButton>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button
              onClick={handleDownloadPrintFile}
              className="w-full bg-blue-600 text-white hover:bg-blue-700"
            >
              Download Print File
            </Button>
            <input
              multiple
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
          </SidepanelSection>
        )}
      </aside>
    </div>
  );
}

export default App;
